"use client"

import { useEffect, useMemo, useRef, useState } from "react"

import type { AffixWrapperProps } from "../type"
import styles from "./lindex.module.scss"

export default function AffixWrapper({
  visibleOffset = 1800,
  baseWidth = 1380,
  bottom = 32,
  unionIconLeft = 20,
  unionIconRight = 20,
  offsetRight = 20,
  hideBelowWidth = 768,
  positionMode = "fixed",
  fixedRight = 20,
  children,
}: AffixWrapperProps) {
  const [mounted, setMounted] = useState(false)
  const [windowWidth, setWindowWidth] = useState(0)
  const [visible, setVisible] = useState(false)
  const visibleRef = useRef(false)

  const unionIconWidth = 40

  useEffect(() => {
    setMounted(true)
    setWindowWidth(window.innerWidth)

    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    let frameId: number | null = null

    const updateVisibility = () => {
      const nextVisible = (document.scrollingElement?.scrollTop ?? window.scrollY) > visibleOffset
      if (nextVisible === visibleRef.current) return
      visibleRef.current = nextVisible
      setVisible(nextVisible)
    }

    const handleScroll = () => {
      if (frameId !== null) return
      frameId = window.requestAnimationFrame(() => {
        frameId = null
        updateVisibility()
      })
    }

    updateVisibility()
    document.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      document.removeEventListener("scroll", handleScroll)
      if (frameId !== null) window.cancelAnimationFrame(frameId)
    }
  }, [visibleOffset])

  const right = useMemo(() => {
    if (!mounted) return 20

    if (positionMode === "fixed") {
      return fixedRight
    }

    if (windowWidth > baseWidth + (unionIconLeft + unionIconWidth + unionIconRight + offsetRight) * 2) {
      return (windowWidth - baseWidth) / 2 - unionIconLeft - unionIconWidth
    }
    return 20
  }, [baseWidth, fixedRight, mounted, offsetRight, positionMode, unionIconLeft, unionIconRight, windowWidth])

  if (!mounted) return null
  if (windowWidth < hideBelowWidth) return null
  if (!visible) return null

  return (
    <div className={styles.affixWrapper} style={{ right, bottom }}>
      {children}
    </div>
  )
}
